import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloseSvg from '@/components/common/CloseSvg';
import EventBus from '@/components/pdp/PdpEventBus';
import { useAddItem } from '@/components/shopping_bag/hooks/useCart';
import { usePwpState } from '@/components/shopping_bag/hooks/usePwpState';
import { QuantitySelector } from '@/components/shopping_bag/QuantitySelector';
import { TmProduct } from '@/tscapi/model/product';
import { Cookie } from '@/tscapi/utils/cookie';

import { en, fr } from '../i18n';

type UpsellItemCardProps = {
    UpsellProducts: TmProduct[];
};

export const UpsellItemCard = ({ UpsellProducts }: UpsellItemCardProps) => {
    const { i18n, t } = useTranslation('addtobag');
    const cookie: Cookie = new Cookie();
    const { savePwpState, modalState } = usePwpState();
    const shouldShowModal = () => {
        if (!modalState?.timestamp) {
            return true; // Show modal if no timestamp exists
        }
        const isStale = Date.now() - modalState.timestamp > 24 * 60 * 60 * 1000; // 24 hours stale time check
        return isStale || !modalState.userPrefersClosed; // Show modal if stale or user doesn't prefer closed
    };
    const [quantity, setQuantity] = useState(1);
    const mutation = useAddItem(cookie.UserAccessToken?.Edp, cookie.CartGuid);
    // Add resource bundles for translations
    React.useEffect(() => {
        i18n.addResourceBundle('en', 'addtobag', en, true, true);
        i18n.addResourceBundle('fr', 'addtobag', fr, true, true);
    }, [i18n]);

    // Return early if no products are provided
    if (!UpsellProducts?.length) {
        return null;
    }
    const handleQuantityChange = (newQuantity: number) => {
        setQuantity(newQuantity); // Update local state
    };
    const handleAddToBag = () => {
        if (!product || !edpData) return;
        mutation.mutate({ itemEDP: edpData.EdpNo, Quantity: quantity }); // Perform the API call
        EventBus.dispatch('change', { edp: edpData.EdpNo, qty: quantity });
    };
    const handleClose = () => {
        savePwpState({ userPrefersClosed: true });
    };
    // Extract product and edpData
    const product = UpsellProducts[0];
    const edpData = product?.Edps?.find((x) => x.EdpNo === product.DefaultEdp);
    const message = product?.ProductMessages?.UpsellMessage?.trim();
    const isUpsellUnlocked = Boolean(message && message.toLowerCase().startsWith('deal'));
    const isRegularUpsell = Boolean(message && message.toLowerCase().startsWith('you'));

    // Return early if product or edpData is invalid
    if (!product || !edpData || !shouldShowModal()) {
        return null;
    }

    return (
        <>
            <div className="relative mb-5 border-2 border-solid p-2.5" style={{ borderColor: '#2a979d' }}>
                <button className="absolute right-2.5 top-2.5 cursor-pointer focus:outline-none" onClick={handleClose}>
                    <span className="visually-hidden">{t('close')}</span>
                    <CloseSvg />
                </button>
                {product?.ProductMessages?.UpsellMessage && (
                    <div className="mb-2.5 inline-flex items-center pr-4">
                        {isUpsellUnlocked && !isRegularUpsell && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 15 15"
                                width="15"
                                height="15"
                                className="mr-1.5 inline-block min-w-[15px]"
                                fill="#01b9bb"
                            >
                                <path d="M14.52 8.27 7.55 1.3a.543.543 0 0 0-.34-.16L1.73.64C1.11.58.59 1.1.64 1.73l.5 5.48c.01.13.07.25.16.34l6.97 6.97c.22.22.57.22.78 0l5.47-5.47c.22-.22.22-.57 0-.78ZM3.81 3.81c-.44.44-1.15.44-1.59 0-.44-.44-.44-1.15 0-1.59.44-.44 1.15-.44 1.59 0s.44 1.15 0 1.59Z" />
                            </svg>
                        )}

                        <span className="text-xsm font-semibold leading-4.5">
                            {product.ProductMessages.UpsellMessage}
                        </span>
                    </div>
                )}
                <div className="grid grid-cols-[96px_auto] grid-rows-[auto_auto_auto] gap-x-4 md:grid-cols-[96px_62px_auto_85px]">
                    {/* Product Image */}
                    <div className="z col-span-1 row-start-1 row-end-3 w-24">
                        <a href={product.ProductLink.LinkWithoutDomain} target="_blank" rel="noreferrer">
                            <img
                                alt={product.Name}
                                id={`badge${product.ItemNo}`}
                                className="img-responsive center-block"
                                width="96"
                                height="96"
                                src={`https://item.tscimg.ca/TSC/${edpData.ImageUrl}?impolicy=M`}
                            />
                        </a>
                    </div>

                    {/* Product Details */}
                    <div className="col-start-2 col-end-3 text-[13px] font-light md:col-end-4">
                        {product.PriceIsLabel && (
                            <div className="mb-2 text-[10px] font-semibold uppercase leading-normal text-black">
                                {product.PriceIsLabel}
                            </div>
                        )}

                        <a href={product.ProductLink.LinkWithoutDomain} target="_blank" rel="noreferrer">
                            <span className="mb-2 line-clamp-2 text-[13px] font-semibold leading-tight">
                                {product.Name}
                            </span>
                        </a>
                    </div>
                    {(isUpsellUnlocked || isRegularUpsell) && (
                        <div className="col-start-2 col-end-3 row-start-2 row-end-3 mb-2.5 mt-2.5 flex items-end">
                            <QuantitySelector
                                value={quantity}
                                lineId={0}
                                inventory={edpData.Inventory}
                                edpNo={edpData.EdpNo}
                                update={{ mutate: ({ qty }) => handleQuantityChange(qty) }} // Custom update function
                            />
                        </div>
                    )}

                    {/* Product Pricing */}
                    <div className="col-start-2 col-end-3 row-start-3 row-end-4 text-right text-sm md:col-start-4 md:col-end-5 md:row-start-1 md:row-end-2">
                        <div className="flex-col md:flex md:flex-wrap">
                            <span className="font-semibold">{edpData.AppliedPrice}</span>
                            {edpData.AppliedPrice !== edpData.WasPrice && (
                                <span className="ml-2.5 line-through md:ml-0">{edpData.WasPrice}</span>
                            )}
                        </div>
                    </div>
                    <div className="col-start-2 col-end-3 row-start-4 row-end-4 text-right text-sm md:col-start-3 md:col-end-5 md:row-start-2 md:row-end-2">
                        {(isUpsellUnlocked || isRegularUpsell) && (
                            <button
                                type="button"
                                aria-label={t('Add to Bag')}
                                className="mx-auto my-0 mt-3 inline w-full max-w-44 cursor-pointer select-none whitespace-nowrap border border-solid border-black bg-black px-3 py-3.5 text-center text-sm font-semibold text-white"
                                onClick={() => handleAddToBag()}
                            >
                                {t('Add to Bag')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
